import { useRef } from 'react';
import { FieldValues, UseFormReset } from 'react-hook-form';
import useDeepCompareEffect from 'use-deep-compare-effect';

type UseLoadInitialValuesOptions = {
  preserveDirtyValues?: boolean;
  // NOTE(jacob): The record initialValues belongs to (e.g. an order id).
  // A record change always discards dirty values, regardless of preserveDirtyValues.
  recordId?: string;
};

/**
 * A utility hook to use in forms that load initial values
 * async from a backend. Uses the reset action from useForm.
 *
 * react-hook-form caches initial values on first render, so
 * we need to reset the form with the new initial values if
 * they come in after the first render.
 */
export default function useLoadInitialValues<T extends FieldValues>(
  reset: UseFormReset<T>,
  initialValues: T,
  transform?: (initialValues: T) => T,
  options?: UseLoadInitialValuesOptions
): void {
  const previousRecordIdRef = useRef(options?.recordId);

  useDeepCompareEffect(() => {
    if (initialValues) {
      const isSameRecord = previousRecordIdRef.current === options?.recordId;
      previousRecordIdRef.current = options?.recordId;

      const nextInitialValues = transform
        ? transform(initialValues)
        : initialValues;
      const shouldPreserveDirtyValues =
        isSameRecord && options?.preserveDirtyValues;

      reset(
        nextInitialValues,
        shouldPreserveDirtyValues ? { keepDirtyValues: true } : undefined
      );
    }
  }, [reset, initialValues, options?.recordId]);
}
